import React, { useEffect, useState } from 'react'
import SalaryCard from '../components/SalaryCard'
import * as api from '../utils/api/apiClient'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { FcEmptyTrash } from 'react-icons/fc'
import GenerateSalary from '../components/GenerateSalary'

const Salary = () => {
  const { id } = useParams()

  // States for salary calculation
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [taxPercentage, setTaxPercentage] = useState('')
  const [bonus, setBonus] = useState('')
  const [penalty, setPenalty] = useState('')
  const [remark, setRemark] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // States for salary display
  const [salarySlip, setSalarySlip] = useState(null)
  const [employeeSalarySlip, setEmployeeSalarySlip] = useState([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [calculatedData, setCalculatedData] = useState(null)
  const [empty, setEmpty] = useState(false)

  // States for edit form
  const [isOpen, setIsOpen] = useState(false)
  const [salaryId, setSalaryId] = useState('')
  const [editFormData, setEditFormData] = useState({
    payment_done: 'false',
    payment_date: '',
    payment_mode: '',
  })

  console.log(salarySlip)
  console.log(employeeSalarySlip)
  // Function to reset all form fields
  const resetForm = () => {
    setMonth('')
    setYear('')
    setTaxPercentage('')
    setBonus('')
    setPenalty('')
    setRemark('')
  }

  // Function to get current employee's salary slips
  const getSalarySlipOfEmployee = async (id) => {
    try {
      const currentDate = new Date()
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()

      const res = await api.getSalarySlipEmployee(id, currentMonth, currentYear)
      setEmployeeSalarySlip(res.data)
      setEmpty(res.data.length > 0)
      console.log(res.data)
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to fetch salary slips')
      }
    }
  }

  // Function to calculate salary
  const calculateSalary = async (
    id,
    month,
    year,
    taxPercentage,
    bonus,
    penalty
  ) => {
    try {
      const res = await api.calculateMonthSalary(
        id,
        parseInt(month),
        parseInt(year),
        parseFloat(taxPercentage),
        parseFloat(bonus),
        parseFloat(penalty)
      )
      console.log(res.data)
      setSalarySlip(res.data)
      setCalculatedData({
        id,
        month,
        year,
        taxPercentage,
        bonus,
        penalty,
        remark,
      })
      setIsDropdownOpen(true)
      toast.success('Salary slip calculated successfully')
      setEmpty(true)
      resetForm()
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to generate salary slip')
      }
    }
  }

  // Function to handle salary calculation form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!id) {
      toast.error('Please select an employee')
      return
    }
    setSubmitting(true)
    try {
      await calculateSalary(id, month, year, taxPercentage, bonus, penalty)
    } finally {
      setSubmitting(false)
    }
  }

  // Function to generate final salary slip
  const generateSalarySlip = async () => {
    if (!calculatedData) {
      toast.error('No calculated data available')
      return
    }

    const { id, month, year, taxPercentage, bonus, penalty, remark } =
      calculatedData

    try {
      const res = await api.generateSalarySlip(
        id,
        parseInt(month),
        parseInt(year),
        parseFloat(taxPercentage),
        parseFloat(bonus),
        parseFloat(penalty),
        remark
      )

      setSalarySlip(res.data)
      setIsModalOpen(false)
      setIsDropdownOpen(false)
      getSalarySlipOfEmployee(id)
      toast.success('Salary slip generated successfully')
      resetForm()
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to generate salary slip')
      }
    }
  }

  // Function to delete salary slip
  const deleteSalary = async (slipId) => {
    try {
      await api.deleteEmployeeSalary(slipId)
      toast.success('Salary slip deleted successfully')
      getSalarySlipOfEmployee(id) // Refresh the list
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to delete salary slip')
      }
    }
  }

  // Function to handle edit form changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Function to handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    // Format the data before sending to API
    const formattedData = {
      payment_done: editFormData.payment_done === 'true', // Convert string to boolean
      payment_date: editFormData.payment_date,
      payment_mode: editFormData.payment_mode,
    }

    try {
      await api.editEmployeeSalary(salaryId, formattedData)
      toast.success('Salary slip updated successfully')
      setIsOpen(false)
      getSalarySlipOfEmployee(id) // Refresh the list
      setEditFormData({
        payment_done: 'false',
        payment_date: '',
        payment_mode: '',
      })
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message)
      } else {
        toast.error('Failed to update salary slip')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Load employee salary slips when component mounts
  useEffect(() => {
    if (id) {
      getSalarySlipOfEmployee(id)
    }
  }, [id])

  return (
    <div className='container mx-auto p-6 bg-cream h-max'>
      {/* Header */}
      <div className='flex justify-between'>
        <h1 className='text-2xl text-brown font-bold'>Employees Salary</h1>

        <button
          onClick={() => setIsModalOpen(!isModalOpen)}
          className='bg-tan p-2 text-white cursor-pointer rounded-md'
        >
          Calculate Salary
        </button>
      </div>

      {/* Calculate Salary Form */}
      {isModalOpen && (
        <div className='w-full my-4'>
          <div className='bg-white p-6 rounded-lg w-full'>
            <h2 className='text-xl font-bold mb-4'>Generate Salary Slip</h2>
            <form onSubmit={handleSubmit} className='space-y-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                {/* Month Input */}
                <div>
                  <label className='block mb-2 font-medium'>Month</label>
                  <input
                    type='number'
                    min='1'
                    max='12'
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                    required
                  />
                </div>

                {/* Year Input */}
                <div>
                  <label className='block mb-2 font-medium'>Year</label>
                  <input
                    type='number'
                    min='2000'
                    max='2100'
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                    required
                  />
                </div>

                {/* Tax Percentage Input */}
                <div>
                  <label className='block mb-2 font-medium'>
                    Tax Percentage
                  </label>
                  <input
                    type='number'
                    min='0'
                    max='100'
                    step='0.01'
                    value={taxPercentage}
                    onChange={(e) => setTaxPercentage(e.target.value)}
                    className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                    required
                  />
                </div>

                {/* Bonus Input */}
                <div>
                  <label className='block mb-2 font-medium'>Bonus</label>
                  <input
                    type='number'
                    min='0'
                    step='0.01'
                    value={bonus}
                    onChange={(e) => setBonus(e.target.value)}
                    className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                    required
                  />
                </div>

                {/* Penalty Input */}
                <div>
                  <label className='block mb-2 font-medium'>Penalty</label>
                  <input
                    type='number'
                    min='0'
                    step='0.01'
                    value={penalty}
                    onChange={(e) => setPenalty(e.target.value)}
                    className='w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500'
                    required
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className='flex justify-end space-x-2 mt-6'>
                <button
                  type='button'
                  onClick={() => {
                    setIsModalOpen(false)
                    resetForm()
                  }}
                  className='px-4 py-2 bg-rust text-white rounded hover:bg-rust/50 transition'
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='px-4 py-2 bg-link text-white rounded hover:bg-link/50 transition'
                  disabled={submitting}
                >
                  {submitting ? 'Calculating...' : 'Calculate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary Slips List */}
      {/* <div className='my-5'>
        {employeeSalarySlip.map((item, i) => (
          <div key={i}>
            <div className='flex gap-4 justify-end my-4'>
              <button
                onClick={() => {
                  setSalaryId(item.id)
                  setIsOpen(true)
                }}
                className='bg-rust p-2 text-white cursor-pointer rounded-md'
              >
                Edit Slip
              </button>
              <button
                onClick={() => deleteSalary(item.id)}
                className='bg-rust p-2 text-white cursor-pointer rounded-md'
              >
                Delete Slip
              </button>
            </div>
            <div>
              <SalaryCard data={item} />
            </div>
          </div>
        ))}
      </div> */}

      {/* Salary Slips List */}
      <div className='my-5 space-y-4'>
        {employeeSalarySlip.map((item) => (
          <SalaryCard
            key={item.id}
            salary={item}
            onEdit={() => {
              setSalaryId(item.id)
              setIsOpen(true)
            }}
            onDelete={() => deleteSalary(item.id)}
          />
        ))}
      </div>

      {/* Edit Salary Form Modal */}
      {isOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg p-6 w-full max-w-md'>
            <h2 className='text-xl font-bold mb-4 text-brown'>
              Update Payment Details
            </h2>

            <form onSubmit={handleEditSubmit} className='space-y-4'>
              {/* Payment Status */}
              <div className='space-y-2'>
                <label className='block text-sm font-medium text-brown'>
                  Payment Status
                </label>
                <select
                  name='payment_done'
                  value={editFormData.payment_done}
                  onChange={handleEditFormChange}
                  className='w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-tan'
                  required
                >
                  <option value='false'>Pending</option>
                  <option value='true'>Completed</option>
                </select>
              </div>

              {/* Payment Date */}
              <div className='space-y-2'>
                <label className='block text-sm font-medium text-brown'>
                  Payment Date
                </label>
                <input
                  type='date'
                  name='payment_date'
                  value={editFormData.payment_date}
                  onChange={handleEditFormChange}
                  className='w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-tan'
                  required
                />
              </div>

              {/* Payment Mode */}
              <div className='space-y-2'>
                <label className='block text-sm font-medium text-brown'>
                  Payment Mode
                </label>
                <select
                  name='payment_mode'
                  value={editFormData.payment_mode}
                  onChange={handleEditFormChange}
                  className='w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-tan'
                  required
                >
                  <option value=''>Select Payment Mode</option>
                  <option value='CASH'>Cash</option>
                  <option value='BANK_TRANSFER'>Bank Transfer</option>
                  <option value='CHECK'>Check</option>
                  <option value='UPI'>UPI</option>
                </select>
              </div>

              {/* Modal Buttons */}
              <div className='flex justify-end space-x-3 mt-6'>
                <button
                  type='button'
                  onClick={() => setIsOpen(false)}
                  className='px-4 py-2 text-sm font-medium text-brown bg-gray-100 rounded-md hover:bg-gray-200'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={submitting}
                  className='px-4 py-2 text-sm font-medium text-white bg-tan rounded-md hover:bg-tan/80 disabled:opacity-50'
                >
                  {submitting ? 'Updating...' : 'Update Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show empty state if no salary slips exist */}
      {!empty && (
        <div className='col-span-full w-full my-8 bg-gradient-to-br from-cream to-beige rounded-lg border border-tan h-96 md:h-[500px] flex justify-center items-center'>
          <div className='text-center'>
            <FcEmptyTrash className='mx-auto h-16 w-16 md:h-24 md:w-24 text-red-500' />
            <p className='text-xl md:text-2xl text-brown mt-4'>Not Found!</p>
            <p className='text-base md:text-xl text-brown mt-2'>
              No salary slips available for this employee!
            </p>
          </div>
        </div>
      )}

      {/* Show calculated salary preview */}
      {isDropdownOpen && (
        <div>
          <GenerateSalary salary={salarySlip} />
          <div className='flex justify-end'>
            <button
              onClick={generateSalarySlip}
              className='bg-tan p-2 my-4 text-white cursor-pointer rounded-md'
            >
              Generate Salary
            </button>
          </div>
        </div>
      )}
      {/* {isDropdownOpen && (
        <div className='fixed inset-0 flex items-center justify-center z-50'>
         
          <div
            className='absolute inset-0 bg-black/40 backdrop-blur-sm'
            onClick={() => setIsDropdownOpen(false)}
          />

         
          <div className='relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 transform transition-all'>
         
            <div className='flex items-center justify-between p-6 border-b'>
              <h2 className='text-xl font-semibold text-gray-800'>
                Salary Details
              </h2>
              <button
                onClick={() => setIsDropdownOpen(false)}
                className='text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100'
              >
                <svg
                  className='w-5 h-5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

           
            <div className='p-6 space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
               
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <p className='text-sm text-gray-500'>Basic Salary</p>
                  <p className='text-lg font-semibold text-gray-800'>
                    ₹{salarySlip.basic_salary}
                  </p>
                </div>

                <div className='bg-gray-50 p-4 rounded-lg'>
                  <p className='text-sm text-gray-500'>Bonus</p>
                  <p className='text-lg font-semibold text-gray-800'>
                    ₹{salarySlip.bonus}
                  </p>
                </div>

                <div className='bg-gray-50 p-4 rounded-lg'>
                  <p className='text-sm text-gray-500'>DA Percentage</p>
                  <p className='text-lg font-semibold text-gray-800'>
                    {salarySlip.da_percentage}%
                  </p>
                </div>

                <div className='bg-gray-50 p-4 rounded-lg'>
                  <p className='text-sm text-gray-500'>Gross Salary</p>
                  <p className='text-lg font-semibold text-green-600'>
                    ₹{salarySlip.gross_salary}
                  </p>
                </div>

                <div className='col-span-2 bg-gray-50 p-4 rounded-lg'>
                  <p className='text-sm text-gray-500'>Penalty</p>
                  <p className='text-lg font-semibold text-red-600'>
                    ₹{salarySlip.penalty}
                  </p>
                </div>
              </div>
            </div>

            
            <div className='p-6 border-t flex justify-end space-x-4'>
              <button
                onClick={() => setIsDropdownOpen(false)}
                className='px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={generateSalarySlip}
                className='px-4 py-2 text-white bg-tan hover:bg-tan/80 rounded-lg transition-colors'
              >
                Generate Slip
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  )
}
export default Salary
